import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, LogOut, User as UserIcon, AlertCircle, Calendar, CheckCircle2, Clock, Menu, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPut } from '../lib/api';
import { toast } from 'sonner';
import GlobalSearch from './GlobalSearch';
import CompanySwitcher from './CompanySwitcher';

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';

export default function Header({ onOpenMenu }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [tasks, setTasks] = useState({ overdue: [], due_today: [], counts: {} });
  const notifRef = useRef(null);
  const date = new Date().toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

  const loadTasks = async () => {
    try {
      // scope=mine so the bell only counts tasks owned by current user
      // (matches My Tasks page default), preventing the 2-vs-0 mismatch.
      const res = await apiGet('/crm/tasks/my?scope=mine');
      setTasks(res);
    } catch {
      // Silent: header should never block on this
    }
  };

  // Mark a single follow-up as done from the bell.
  const markDone = async (taskId, e) => {
    e?.stopPropagation();
    if (!taskId) return;
    try {
      await apiPut(`/crm/tasks/${taskId}/complete`, {});
      toast.success('Marked as done');
      loadTasks();
    } catch {
      toast.error('Could not mark as done');
    }
  };

  // Bulk dismiss all overdue+due_today items in the bell.
  const markAllRead = async () => {
    const all = [...(tasks.overdue || []), ...(tasks.due_today || [])];
    if (!all.length) return;
    if (!window.confirm(`Mark ${all.length} item(s) as done?`)) return;
    let okCount = 0;
    for (const t of all) {
      try { await apiPut(`/crm/tasks/${t.id}/complete`, {}); okCount++; } catch { /* ignore */ }
    }
    toast.success(`Marked ${okCount} of ${all.length} as done`);
    loadTasks();
  };

  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 60_000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    if (notifOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [notifOpen]);

  const overdueCount = tasks.counts?.overdue || 0;
  const dueTodayCount = tasks.counts?.due_today || 0;
  const totalAlerts = overdueCount + dueTodayCount;

  return (
    <header
      data-testid="top-header"
      className="bg-white border-b border-slate-200 flex items-center px-3 sm:px-5 gap-2 sm:gap-4 shrink-0"
      style={{
        // v1.0.13 — reserve the system status-bar inset on phones / PWA / AAB
        // so the white header doesn't sit behind the clock & notification icons.
        paddingTop: 'env(safe-area-inset-top)',
        minHeight: 'calc(4rem + env(safe-area-inset-top))',
      }}
    >
      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={onOpenMenu}
        className="md:hidden w-10 h-10 flex items-center justify-center text-slate-700 hover:bg-slate-100 rounded-sm shrink-0"
        aria-label="Open menu"
        data-testid="mobile-menu-btn"
      >
        <Menu size={20} />
      </button>

      <GlobalSearch />

      <CompanySwitcher />

      <div className="hidden lg:block text-xs font-mono text-slate-500 px-3 border-l border-slate-200">{date}</div>

      <div className="relative" ref={notifRef}>
        <button
          data-testid="notification-bell"
          onClick={() => setNotifOpen(!notifOpen)}
          className="relative w-10 h-10 flex items-center justify-center text-slate-500 hover:text-[#1F7A6E] hover:bg-slate-50 rounded-sm"
        >
          <Bell size={17} />
          {totalAlerts > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold flex items-center justify-center text-white rounded-full ${
                overdueCount > 0 ? 'bg-red-600' : 'bg-amber-500'
              }`}
              data-testid="notification-badge"
            >
              {totalAlerts > 99 ? '99+' : totalAlerts}
            </span>
          )}
        </button>

        {notifOpen && (
          <div className="absolute right-0 top-12 w-[calc(100vw-1rem)] max-w-[24rem] bg-white border border-slate-200 rounded-sm shadow-xl z-30" data-testid="notification-panel">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
              <div className="font-display font-bold text-sm">Notifications</div>
              <div className="flex items-center gap-2">
                {totalAlerts > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    className="text-[10px] text-slate-500 hover:text-[#1F7A6E] font-display font-bold uppercase tracking-wider"
                    data-testid="notif-mark-all-read"
                    title="Mark all overdue & due-today follow-ups as done"
                  >
                    Mark all read
                  </button>
                )}
                <Link to="/app/crm/tasks" onClick={() => setNotifOpen(false)} className="text-xs text-[#1F7A6E] hover:underline font-display font-bold uppercase tracking-wider">View all</Link>
              </div>
            </div>

            {totalAlerts === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-slate-500">
                <CheckCircle2 size={24} className="text-emerald-500 mx-auto mb-2" />
                You're all caught up. No tasks need attention.
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto">
                {overdueCount > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-red-50 border-b border-red-100 flex items-center gap-2 text-red-700">
                      <AlertCircle size={13} />
                      <span className="text-[10px] font-display font-bold uppercase tracking-[0.16em]">Overdue · {overdueCount}</span>
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {tasks.overdue.slice(0, 5).map(t => (
                        <li key={t.id} className="px-4 py-2.5 hover:bg-slate-50 flex items-start gap-2 group">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-display font-semibold text-[#0F172A] truncate">{t.description}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                              <Clock size={10} /> Due {fmtDate(t.reminder_date)}
                              {t.entity_label && <span className="truncate">· {t.entity_label}</span>}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => markDone(t.id, e)}
                            className="shrink-0 p-1 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                            data-testid={`notif-mark-done-${t.id}`}
                            title="Mark this follow-up as done"
                          >
                            <Check size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {dueTodayCount > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2 text-amber-700">
                      <Calendar size={13} />
                      <span className="text-[10px] font-display font-bold uppercase tracking-[0.16em]">Due Today · {dueTodayCount}</span>
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {tasks.due_today.slice(0, 5).map(t => (
                        <li key={t.id} className="px-4 py-2.5 hover:bg-slate-50 flex items-start gap-2 group">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-display font-semibold text-[#0F172A] truncate">{t.description}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                              {t.entity_label && <span className="truncate">{t.entity_label}</span>}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => markDone(t.id, e)}
                            className="shrink-0 p-1 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                            data-testid={`notif-mark-done-${t.id}`}
                            title="Mark this follow-up as done"
                          >
                            <Check size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="relative">
        <button
          data-testid="user-menu-btn"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 pl-2 pr-3 py-1.5 hover:bg-slate-50 rounded-sm"
        >
          <div className="w-8 h-8 rounded-sm bg-[#2F3E46] text-white flex items-center justify-center font-display font-bold text-xs overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name || 'U').slice(0,1).toUpperCase()}
          </div>
          <div className="text-left hidden sm:block">
            <div className="text-xs font-display font-semibold leading-tight">{user?.name || 'User'}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{user?.role || 'Member'}</div>
          </div>
          <ChevronDown size={14} className="text-slate-400" />
        </button>
        {open && (
          <div className="absolute right-0 top-12 w-56 bg-white border border-slate-200 rounded-sm shadow-lg z-30 py-1">
            <div className="px-4 py-3 border-b border-slate-100">
              <div className="font-display font-semibold text-sm truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <button data-testid="profile-link" onClick={() => { setOpen(false); navigate('/app/profile'); }} className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"><UserIcon size={14} /> My Profile</button>
            <button data-testid="logout-btn" onClick={logout} className="w-full text-left px-4 py-2 text-sm hover:bg-teal-50 text-[#1F7A6E] flex items-center gap-2"><LogOut size={14} /> Sign out</button>
          </div>
        )}
      </div>
    </header>
  );
}


